import { useState, useEffect } from "react";
import api from "../../api/axios";
import {
    Users,
    ShieldAlert,
    Activity,
    FileCheck,
    Server
} from "lucide-react";
import { motion } from "framer-motion";

const StatCard = ({ label, value, icon: Icon, color, loading }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/5 border border-white/10 rounded-xl p-6 relative overflow-hidden group hover:border-white/20 transition-all"
    >
        <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${color}`}>
            <Icon size={64} />
        </div>
        <div className="relative z-10">
            <div className={`p-3 rounded-lg w-fit mb-4 bg-white/5 ${color}`}>
                <Icon size={24} />
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">
                {loading ? <span className="animate-pulse">...</span> : value}
            </h3>
            <p className="text-xs text-gray-500 uppercase tracking-widest">{label}</p>
        </div>
    </motion.div>
);

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        try {
            const res = await api.get("/admin/pulse");
            setStats(res.data);
        } catch (err) {
            console.error("Pulse dead:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
        const interval = setInterval(fetchStats, 30000); // 30s Heartbeat
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h2 className="text-3xl font-light text-white">
                    System <span className="font-bold text-red-500">Pulse</span>
                </h2>
                <p className="text-gray-500 mt-2">Real-time surveillance of Antaryatra network.</p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    label="Total Souls"
                    value={stats?.users}
                    icon={Users}
                    color="text-blue-500"
                    loading={loading}
                />
                <StatCard
                    label="Safety Alerts"
                    value={stats?.safetyAlerts}
                    icon={ShieldAlert}
                    color="text-red-500"
                    loading={loading}
                />
                <StatCard
                    label="Pending Action"
                    value={stats?.pendingVerifications}
                    icon={FileCheck}
                    color="text-yellow-500"
                    loading={loading}
                    subtitle={
                        stats?.pendingApplications > 0 || stats?.pendingProfiles > 0
                        ? `${stats.pendingApplications} Apps + ${stats.pendingProfiles} Audits`
                        : "No pending work"
                    }
                />
                <StatCard
                    label="Pending Reports"
                    value={stats?.pendingReports}
                    icon={Activity}
                    color="text-orange-500"
                    loading={loading}
                />
            </div>

            {/* System Status */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Defcon Level */}
                <div className="lg:col-span-2 bg-gradient-to-br from-red-900/20 to-black border border-red-900/30 rounded-2xl p-8 flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-2">Threat Level</h3>
                        <p className="text-gray-400 text-sm max-w-md">
                            Automatic assessment based on flag velocity and report volume.
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-6xl font-black text-red-600 tracking-tighter">
                            DEFCON {stats?.defcon || 5}
                        </div>
                        <div className="text-xs text-red-400 mt-1 uppercase tracking-widest">
                            {stats?.defcon === 5 ? "Normal Operations" : "Elevated Risk"}
                        </div>
                    </div>
                </div>

                {/* Server Status */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col justify-center items-center text-center">
                    <div className={`p-4 rounded-full mb-4 ${stats?.serverStatus === "OPERATIONAL" ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"}`}>
                        <Server size={32} />
                    </div>
                    <h3 className="text-white font-bold mb-1">Server Status</h3>
                    <p className="text-sm text-gray-400">{stats?.serverStatus || "Unknown"}</p>
                </div>
            </div>
        </div>
    );
}
